import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import styled, { css } from 'styled-components';

import { OptionPropType } from '@components/basting-selector/prop-types';
import { BADGE_TYPES, colors } from '@nandosaus/constants';
import { TextPill } from '@components/text-pill';
import { Badge } from '../../../badge';
import { Box, Flex } from '../../../grid';
import { OptionImage } from './option-image';
import { P } from '../../../typography/new';
import { CheckIndicator } from '../../../check-indicator';
import { RoundRadio } from '../choice-types/mixed-basting';

const BestValuePill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 22px;
  padding: 0 8px;
  margin-left: 8px;
  border-radius: 38px;
  background: linear-gradient(101deg, #640b14 -1.68%, #f7443e 76.12%);
  vertical-align: middle;
`;

const Button = styled(Box).attrs(() => ({
  as: 'button',
  display: 'flex',
  flexDirection: 'column',
  width: '124px',
  overflow: 'hidden',
  textAlign: 'left',
  height: '100%',
}))``;

const OptionCard = React.forwardRef(({ option, label, isDrink, ...radixProps }, forwardedRef) => {
  const state = radixProps['data-state'];
  const checked = state === 'checked';

  return (
    <Button ref={forwardedRef} {...radixProps}>
      <Box as="span" display="block" width="100%" position="relative">
        <OptionImage option={option} selected={checked} height={124} width={124} isDrink={isDrink} />
        <CheckIndicator checked={checked} top="8px" backgroundColor="black" />
        {option.disabled ? (
          <Badge as="span" type="grey" position="absolute" bottom="5px" left="5px">
            <P variant="small-semi-bold">Sold Out</P>
          </Badge>
        ) : (
          option.isEligibleForDeliveryDiscount && (
            <Badge
              position="absolute"
              type="red"
              width="100%"
              bottom="8px"
              left="8px"
              fontSize={{ xs: '7px', lg: '11px' }}
            >
              Get free delivery
            </Badge>
          )
        )}
        {!option.disabled &&
          option.tags?.map?.(({ label: tagLabel }) => (
            <Badge
              position="absolute"
              type={tagLabel === 'Our Choice' ? BADGE_TYPES.HIGHLIGHTED : 'teal'}
              width="100%"
              bottom="7px"
              left="7px"
              pb="3px"
              pt="1px"
              fontSize={{ xs: '7px', lg: '11px' }}
            >
              <P variant="small-semi-bold">{tagLabel}</P>
            </Badge>
          ))}
      </Box>
      <Box
        as="span"
        display="flex"
        flexDirection="column"
        pt="8px"
        px="8px"
        flexGrow={1}
        justifyContent="space-between"
        width="100%"
      >
        <P as="span" variant="small-semi-bold" color={option.disabled ? 'grey30' : 'black'}>
          {label || option.label}
        </P>
        {option.text && (
          <P as="span" pt="6px" variant="extra-small" color={option.disabled ? 'grey30' : 'black'}>
            {option.text}
          </P>
        )}
      </Box>
    </Button>
  );
});

OptionCard.propTypes = {
  option: OptionPropType.isRequired,
  label: PropTypes.string,
  isDrink: PropTypes.bool,
};

OptionCard.defaultProps = {
  label: undefined,
  isDrink: false,
};

const ComposedOptionCard = observer(OptionCard);

const OptionLabelButton = styled.button`
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  border-radius: 4px;
  overflow: hidden;
  text-align: left;
  height: 100%;
  align-items: center;
  padding: 4px 0;
  border: none;
  background: transparent;
  cursor: pointer;

  /* Borders only when an image is shown */
  ${({ $showOptionImage }) =>
    $showOptionImage &&
    css`
      border-top: 1px solid grey100; /* every item in lists that opt-in */
    `};

  &:disabled {
    cursor: not-allowed;
  }

  @media (min-width: 1024px) {
    border-radius: 8px;
  }
`;

const OptionLabel = React.forwardRef(({ option, showOptionImage, label, isDrink, ...radixProps }, forwardedRef) => {
  const state = radixProps['data-state'];
  const checked = state === 'checked';

  const hasImage = showOptionImage && !!option.image;

  return (
    <OptionLabelButton ref={forwardedRef} $showOptionImage={showOptionImage} {...radixProps}>
      <Flex width="100%" alignItems="center" gap="16px">
        <Box flex={1}>
          <Box display="flex" flexDirection="row" alignItems="center">
            <RoundRadio checked={checked} />
            <P as="span" color={option.disabled ? 'grey300' : 'black'} variant="standard-semi-bold">
              {label || option.label}
              {option.tags?.map?.(({ label: tagLabel }) => {
                if (tagLabel === 'Best Value') {
                  return (
                    <BestValuePill key={tagLabel}>
                      <P variant="extra-small-semi-bold" color="white" lineHeight={0} as="span">
                        {tagLabel}
                      </P>
                    </BestValuePill>
                  );
                }

                const tagColorMap = {
                  'Our Choice': colors.badgeYellow,
                };
                const tagColor = tagColorMap[tagLabel] || colors.neonTeal400;

                return (
                  <TextPill
                    key={tagLabel}
                    text={tagLabel}
                    borderRadius="100px"
                    color="black"
                    backgroundColor={tagColor}
                    height={{ _: '20px', lg: '22px' }}
                    width="auto"
                    px="8px"
                    display="inline-flex"
                    marginLeft="8px"
                    textTransform="capitalize"
                  />
                );
              })}
              {(option.text || option.isEligibleForDeliveryDiscount) && (
                <Box display="flex" flexDirection="row" alignItems="flex-end" width="100%" gap="8px" marginTop="4px">
                  {option.text && (
                    <P as="span" variant="extra-small" color={option.disabled ? 'grey300' : 'grey70'}>
                      {option.disabled ? 'Sold Out' : option.text}
                    </P>
                  )}
                  {option.isEligibleForDeliveryDiscount && (
                    <P as="span" variant="extra-small" color={option.disabled ? 'grey300' : 'grey70'}>
                      Get Free Delivery
                    </P>
                  )}
                </Box>
              )}
            </P>
          </Box>
        </Box>
        {hasImage && <OptionImage selected={checked} option={option} height={64} width={64} isDrink={isDrink} />}
      </Flex>
    </OptionLabelButton>
  );
});

OptionLabel.propTypes = {
  option: OptionPropType.isRequired,
  label: PropTypes.string,
  showOptionImage: PropTypes.bool,
  isDrink: PropTypes.bool,
};

OptionLabel.defaultProps = {
  label: undefined,
  showOptionImage: true,
  isDrink: false,
};

const ComposedOptionLabel = observer(OptionLabel);

export { ComposedOptionCard as OptionCard, ComposedOptionLabel as OptionLabel };
